import { useCallback, useReducer } from 'react';

type UpdateAction<State> = {
  type: 'update';
  payload: Partial<State>;
};

type ResetAction<State> = {
  type: 'reset';
  payload: State;
};

export default function useResettableState<State>(initialState: State): [
  State & { touched: boolean },
  {
    update: (payload: Partial<State>) => void;
    reset: (payload: State) => void;
  },
] {
  const [state, dispatch] = useReducer(
    (
      currentState: State & { touched: boolean },
      action: UpdateAction<State> | ResetAction<State>,
    ): State & { touched: boolean } => {
      if (action.type === 'reset') {
        return { ...action.payload, touched: false };
      }

      return {
        ...currentState,
        ...action.payload,
        touched: true,
      };
    },
    { ...initialState, touched: false },
  );

  const update = useCallback((payload: Partial<State>) => {
    dispatch({ type: 'update', payload });
  }, []);

  const reset = useCallback((payload: State) => {
    dispatch({ type: 'reset', payload });
  }, []);

  return [state, { update, reset }];
}
